import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Tooltip } from '@blade/ui-kit'
import { PairLink } from '@root/entities/pair/ui/common'
import { FlashTextContentDistinct } from '@root/features/flash/flash-text-content-distinct'
import { CountMax3DigitsFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import type { PairMetrics, PairModel } from '@root/entities/pair'

type Props = {
  period: PairMetrics.MetricsSpan
  pair: PairModel
}

export const ExploreTableSwapsCell: React.FC<Props> = ({ pair, period }) => {
  const { t } = useTranslation()
  const [buy] = useAtom(pair.stats[period].buys)
  const [sell] = useAtom(pair.stats[period].sells)

  const total = buy + sell

  const tooltipContent = useMemo(() => {
    if (buy > 0 && sell === 0) {
      return (
        <SwapsTable>
          <div>{t('trading.buys')}</div>
          <FlashTextContentDistinct>{buy}</FlashTextContentDistinct>
        </SwapsTable>
      )
    }
    if (buy === 0 && sell > 0) {
      return (
        <SwapsTable>
          <div>{t('trading.sells')}</div>
          <FlashTextContentDistinct>{sell}</FlashTextContentDistinct>
        </SwapsTable>
      )
    }
    if (buy > 0 && sell > 0) {
      return (
        <SwapsTable>
          <div>{t('trading.buys')}</div>
          <FlashTextContentDistinct>{buy}</FlashTextContentDistinct>
          <div>{t('trading.sells')}</div>
          <FlashTextContentDistinct>{sell}</FlashTextContentDistinct>
        </SwapsTable>
      )
    }
    return t('common.n_a')
  }, [buy, sell, t])

  return (
    <Tooltip content={tooltipContent} align="center">
      <Root pair={pair}>
        <SwapIcon buy={buy} sell={sell} />
        <FlashTextContentDistinct>
          <CountMax3DigitsFormatted count={total} />
        </FlashTextContentDistinct>
      </Root>
    </Tooltip>
  )
}

const Root = styled(PairLink<PairModel>)`
  pointer-events: auto;
  color: var(--text-11);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px;

  align-items: center;
`

const SwapIcon: React.FC<{ buy: number; sell: number }> = ({ buy, sell }) => {
  const total = buy + sell

  if (total === 0) {
    return <EmptyBar />
  }

  return (
    <Bar>
      <SellPart style={{ height: `${(sell / total) * 100}%` }} />
    </Bar>
  )
}

const EmptyBar = styled.div`
  width: 4px;
  height: 24px;
  border-radius: 1px;
  background-color: var(--appearance-inv-1);
  opacity: 0.3333;
`

const Bar = styled.div`
  width: 4px;
  height: 24px;
  border-radius: 1px;
  background-color: var(--up-9);
  overflow: hidden;
  position: relative;
`

const SellPart = styled.div`
  top: 0;
  width: 100%;
  background-color: var(--down-9);
`

const SwapsTable = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 6px;
`
